import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { ListOwnership } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";

import { TabChip } from "@/components/TabChip/TabChip";

import { PinnedFilterTabButton } from "../../List/components/PinnedFilterTabButton";
import { CampaignPinnedFilterTab } from "../utils/campaignPinnedFilterTabs";

export type CampaignsOwnershipCountMap = Partial<Record<ListOwnership, number>>;

interface CampaignsOwnershipTabsProps {
	value: ListOwnership;
	onChange: (value: ListOwnership) => void;
	/** Per-scope campaign counts for the tab bubbles; a bubble is hidden when 0/absent. */
	counts?: CampaignsOwnershipCountMap;
	/** User-pinned filter tabs rendered after the built-in tabs (PREC-3880). */
	pinnedTabs?: CampaignPinnedFilterTab[];
	/** Id of the pinned tab whose snapshot matches the current filters, if any. */
	activePinnedTabId?: number | null;
	onSelectPinnedTab?: (tab: CampaignPinnedFilterTab) => void;
	onRenamePinnedTab?: (id: number, name: string) => void;
	onUpdatePinnedTab?: (id: number) => void;
	onDeletePinnedTab?: (id: number) => void;
}

/**
 * Ownership tab strip of the Campaigns overview (My / Team / All campaigns, PREC-3880). A sibling
 * of the List overview's `ListOwnershipTabs` — same look, and the bubbles count campaigns (via the
 * campaigns-overview stats) instead of lists. There is no search axis here.
 *
 * User-pinned filter tabs are rendered after the built-in tabs, separated by a divider. While a
 * pinned tab is active the built-in tabs are not highlighted, so the strip always shows exactly
 * one selected tab.
 */
export const CampaignsOwnershipTabs: FunctionComponent<CampaignsOwnershipTabsProps> = ({
	value,
	onChange,
	counts,
	pinnedTabs = [],
	activePinnedTabId = null,
	onSelectPinnedTab,
	onRenamePinnedTab,
	onUpdatePinnedTab,
	onDeletePinnedTab,
}) => {
	const { t } = useTranslation();

	const tabs: { label: string; value: ListOwnership }[] = [
		{ label: t("campaignsOverview.tabs.mine"), value: ListOwnership.MINE },
		{ label: t("campaignsOverview.tabs.team"), value: ListOwnership.TEAM },
		{ label: t("campaignsOverview.tabs.all"), value: ListOwnership.ALL },
	];

	return (
		<div className="flex bg-white border-b border-border-light w-full flex-shrink-0">
			{tabs.map((tab) => {
				const isActive = value === tab.value && !activePinnedTabId;
				const count = counts?.[tab.value];

				return (
					<button
						key={tab.value}
						className={classNames(
							"inline-flex items-center px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer",
							isActive
								? "border-brand-light text-brand-light"
								: "border-transparent text-text-meta hover:text-text hover:border-gray-300"
						)}
						type="button"
						onClick={() => onChange(tab.value)}
					>
						{tab.label}
						{typeof count === "number" && count > 0 && (
							<TabChip active={isActive} label={`${count}`} />
						)}
					</button>
				);
			})}

			{pinnedTabs.length > 0 && (
				<div className="my-2 mx-1.5 w-px bg-border-light" aria-hidden="true" />
			)}

			{pinnedTabs.map((tab) => (
				<PinnedFilterTabButton
					key={tab.id}
					isActive={activePinnedTabId === tab.id}
					tab={tab}
					onDelete={() => onDeletePinnedTab?.(tab.id)}
					onRename={(name) => onRenamePinnedTab?.(tab.id, name)}
					onSelect={() => onSelectPinnedTab?.(tab)}
					onUpdate={() => onUpdatePinnedTab?.(tab.id)}
				/>
			))}
		</div>
	);
};
